import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { CommentInfoFragment } from 'gqlTypes'
import { useGetActivityFeed } from 'modules/activity/operations'
import { useThreadRouteParams } from 'utils/hooks/useThreadRouteParams'
import { ActivityList } from './ActivityList'
import { ThreadPanel } from './ThreadPanel'
import { ArtboardInSidebar, ShareInSidebar } from 'modules/shares/types'
import { ArtboardSubscriptionToggle } from 'modules/shares/components/ShareMetaSidebar/SubscriptionToggle'
import { routes } from 'constants/routes'
import useUserSignedIn from 'modules/user/useUserSignedIn'
import { breakpoint } from 'utils'

// Styles
const SubscriptionToggleContainer = styled.div<{ userCanInspect: boolean }>`
  ${({ userCanInspect }) =>
    userCanInspect
      ? css`
          padding: 4px 16px 16px;
          margin-top: 4px;
        `
      : css`
          padding: 16px;
        `};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.dark.base};

  ${breakpoint('base', 'sm')`
    padding: 16px;
  `};
`

// Types
export interface ActivityListQueryProps {
  artboard: ArtboardInSidebar
  share: ShareInSidebar
  enabled?: boolean
}

// Components
const ActivityListQuery = ({ artboard, share }: ActivityListQueryProps) => {
  const history = useHistory()
  const isUserSignedIn = useUserSignedIn()

  // This is used to force to scroll to show an specific activity,
  // the main scenario is when coming back from the thread view
  const [activityToShow, setActivityToShow] = useState<string>('')

  const {
    isThreadRoute,
    threadIdentifier,
    isThreadIdentifier,
  } = useThreadRouteParams()

  const { activities, onLoadMore, isLoading } = useGetActivityFeed({
    artboard: {
      identifier: artboard.identifier,
      permanentArtboardShortId: artboard.permanentArtboardShortId,
    },
    share: {
      userCanAccessVersions: share.userCanAccessVersions,
      commentsEnabled: share.commentsEnabled,
      identifier: share.identifier,
      versionTypeAvailableToUser: share.versionTypeAvailableToUser,
    },
  })

  const threadParentComment = activities.find(isThreadIdentifier)

  // In cases where the thread does not exist, the route is replaced by the
  // artboard details
  useEffect(() => {
    // Note: it could happen that everything is correct but we got redirected to
    // the activities, this is the case where `threadIdentifier` exists but not
    // `threadParentComment` because this activity is not in the first page
    if (threadIdentifier && !threadParentComment && !isLoading) {
      history.replace(
        routes.ARTBOARD_DETAIL.create({
          shareID: share.identifier,
          permanentArtboardShortId: artboard.permanentArtboardShortId,
        })
      )
    }
  }, [
    isLoading,
    isThreadRoute,
    threadParentComment,
    threadIdentifier,
    history,
    share.identifier,
    artboard.permanentArtboardShortId,
  ])

  // Keep activityToShow updated when changing from activity
  // view and thread view
  useEffect(() => {
    setActivityToShow(threadParentComment ? threadIdentifier! : '')
  }, [
    activities,
    threadIdentifier,
    isThreadIdentifier,
    setActivityToShow,
    threadParentComment,
  ])

  if (isThreadRoute && threadParentComment) {
    return (
      <ThreadPanel
        comment={threadParentComment as CommentInfoFragment}
        shareIdentifier={share.identifier}
        artboard={artboard}
      />
    )
  }

  return (
    <>
      {!isThreadRoute && isUserSignedIn && share.commentsEnabled && (
        <SubscriptionToggleContainer userCanInspect={share.userCanInspect!}>
          <ArtboardSubscriptionToggle
            id={artboard.identifier}
            permanentArtboardShortId={artboard.permanentArtboardShortId!}
            documentVersionShortId={artboard.documentVersionShortId!}
            status={artboard.subscriptionStatus}
            shareIdentifier={share.identifier}
          />
        </SubscriptionToggleContainer>
      )}

      <ActivityList
        activities={activities}
        onLoadMore={onLoadMore}
        artboard={artboard}
        share={share}
        isLoading={isLoading}
        activityToShow={activityToShow}
      />
    </>
  )
}

export default ActivityListQuery
